import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import env from '~/libs/env'

export const ModelType = Object.freeze({
  OFFER: 'offer',
  OFFER_OPTION: 'offerOption',
  APPROVAL: 'approval',
})

import { useApprovalsStore } from '~/store/approvals'
import { useOfferOptionsStore } from '~/store/offer-options'
import { useDealsStore } from '~/store/deals'

export const useDeclineReasons = defineStore('declineReasons', () => {
  const approvalsStore = useApprovalsStore()
  const offerOptionsStore = useOfferOptionsStore()
  const dealsStore = useDealsStore()
  const nuxtApp = useNuxtApp()
  const { $axios } = nuxtApp

/*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/
  const declineReasons = ref([])
  const loaded = ref(false)

/*
  ██████  ███████ ████████ ████████ ███████ ██████  ███████
 ██       ██         ██       ██    ██      ██   ██ ██
 ██   ███ █████      ██       ██    █████   ██████  ███████
 ██    ██ ██         ██       ██    ██      ██   ██      ██
  ██████  ███████    ██       ██    ███████ ██   ██ ███████
 GETTERS
*/
  const activeDeclineReasons = computed(() => {
    return declineReasons.value
      .filter(({ reason }) => reason.toLowerCase() !== 'other')
      .map(({ id, reason }) => ({
        id,
        reason
      }))
  })
  const otherDeclineReasonId = computed(() => {
    const found = declineReasons.value.find(dr => dr.reason && dr.reason.toLowerCase() === 'other')
    return found ? found.id : null
  })

/*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/
  async function getDeclineReasons(force = false) {
    if (declineReasons.value.length && !force) {
      return
    }

    const { data, status } = await $axios.get(
    `${env('apiUrl')}/decline-reasons`
    )

    const _declineReasons = data && data.data ? data.data : []
    if (status !== 200) {
      return
    }

    declineReasons.value = _declineReasons
    loaded.value = true
  }

  async function saveDeclineReasons(modelType, entityId, declineReasons) {
    if (!entityId || !modelType) throw new Error('Both entityId and modelType are required')
    if (!Array.isArray(declineReasons)) throw new Error('declineReasons must be an array')
    const response = await $axios.put(
      `${env('apiUrl')}/${modelType}/${entityId}/decline`,
      { declineReasons },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    const updatedEntity = response?.data?.data
    if (updatedEntity) {
      if (modelType === ModelType.APPROVAL) {
        approvalsStore.mutateEntity(entityId, updatedEntity)
      } else if (modelType === ModelType.OFFER_OPTION) {
        offerOptionsStore.mutateEntity(entityId, updatedEntity)
      } else if (modelType === ModelType.OFFER) {
        dealsStore.mutateOfferEntity(entityId, updatedEntity)
      }
    }

    return response
  }

  return {
    //STATE
    declineReasons,
    loaded,
    //GETTERS
    activeDeclineReasons,
    otherDeclineReasonId,
    //ACTIONS
    getDeclineReasons,
    saveDeclineReasons,
  }
})
